// Frontmatter form for agent files.
// Uses react-hook-form + zod per plan §1.5.
// Renders different fields depending on whether the file is an Agent or Skill/Sub-skill.
// Ontology spec §10 retrofit (Agent E): optional Object Type binding; when set,
// the trigger-rule Field becomes a typed property dropdown and the Operator list
// is filtered by the chosen property's kind.

import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AgentFileKind, Frontmatter, TriggerOp } from "@/types/agent-skills";
import { useObjectTypes, useObjectType } from "@/hooks/useObjectTypes";
import { compatibleOperators, type PropertyKind } from "@/lib/agent-skills/operatorCompatibility";

// Sentinel value used by the Object Type dropdown to represent "no binding".
const NONE = "__none__";

type FormValues = {
  name: string;
  description: string;
  trigger_type: "description" | "rule";
  trigger_rule_field: string;
  trigger_rule_op: TriggerOp;
  trigger_rule_value: string;
  object_type_api_name: string;
};

interface FrontmatterFormProps {
  /** Current frontmatter values to display. */
  value: Frontmatter;
  /** The kind of file being edited (drives visible fields). */
  kind: AgentFileKind;
  /** Called whenever the user changes any field with the updated Frontmatter. */
  onChange: (next: Frontmatter) => void;
  /** When true, all inputs are disabled (e.g. while saving). */
  disabled?: boolean;
}

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]*$/;

const formSchema = (isAgent: boolean) =>
  z
    .object({
      name: z
        .string()
        .min(1, "Name is required")
        .regex(SLUG_REGEX, "Must be a valid slug (e.g. utility-vendors)"),
      description: z
        .string()
        .min(1, "Description is required")
        .max(500, "Description must be 500 characters or fewer"),
      trigger_type: z.enum(["description", "rule"]).optional(),
      trigger_rule_field: z.string().optional(),
      trigger_rule_op: z.enum(["equals", "contains", "gt", "lt", "regex"] as const).optional(),
      trigger_rule_value: z.string().optional(),
      object_type_api_name: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (isAgent) return;
      if (!data.trigger_type) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["trigger_type"],
          message: "Trigger type must be description or rule",
        });
      }
      if (data.trigger_type === "rule") {
        if (!data.trigger_rule_field || data.trigger_rule_field.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["trigger_rule_field"],
            message: "Field name is required",
          });
        }
        if (!data.trigger_rule_value || data.trigger_rule_value.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["trigger_rule_value"],
            message: "Value is required",
          });
        }
      }
    });

export function FrontmatterForm({ value, kind, onChange, disabled }: FrontmatterFormProps) {
  const isAgent = kind === "agent";
  const schema = formSchema(isAgent);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: value.name ?? "",
      description: value.description ?? "",
      trigger_type: value.trigger_type ?? "description",
      trigger_rule_field: value.trigger_rule?.field ?? "",
      trigger_rule_op: (value.trigger_rule?.op as TriggerOp) ?? "equals",
      trigger_rule_value: String(value.trigger_rule?.value ?? ""),
      object_type_api_name: value.object_type_api_name ?? "",
    },
    mode: "onChange",
  });

  const triggerType = form.watch("trigger_type");
  const objectTypeApiName = form.watch("object_type_api_name");
  const selectedField = form.watch("trigger_rule_field");

  // Object Type lookup for the typed-field dropdown.
  const { objectTypes } = useObjectTypes();
  const selectedObjectTypeId = useMemo(() => {
    if (!objectTypeApiName) return null;
    return objectTypes.find((t) => t.api_name === objectTypeApiName)?.id ?? null;
  }, [objectTypeApiName, objectTypes]);

  // Warn (non-blocking) when a stored object_type_api_name does not resolve.
  const objectTypeMissing = Boolean(
    objectTypeApiName && objectTypes.length > 0 && !selectedObjectTypeId
  );

  const { objectType } = useObjectType(selectedObjectTypeId);
  const propertyKindByApiName = useMemo(() => {
    const map = new Map<string, PropertyKind>();
    for (const p of objectType?.property_types ?? []) {
      map.set(p.api_name, p.kind as PropertyKind);
    }
    return map;
  }, [objectType]);

  const selectedPropertyKind = propertyKindByApiName.get(selectedField) ?? null;
  const availableOperators = useMemo(
    () => compatibleOperators(selectedPropertyKind),
    [selectedPropertyKind]
  );

  // If the current operator is incompatible with the selected property kind,
  // reset it to the first compatible op.
  useEffect(() => {
    const currentOp = form.getValues("trigger_rule_op");
    if (!availableOperators.includes(currentOp) && availableOperators.length > 0) {
      form.setValue("trigger_rule_op", availableOperators[0], { shouldDirty: true });
    }
  }, [availableOperators, form]);

  // Sync form values → parent onChange whenever any field changes.
  useEffect(() => {
    const sub = form.watch((vals) => {
      const next: Frontmatter = {
        name: vals.name ?? "",
        description: vals.description ?? "",
      };
      if (!isAgent) {
        next.trigger_type = vals.trigger_type ?? "description";
        if (vals.trigger_type === "rule") {
          next.trigger_rule = {
            field: vals.trigger_rule_field ?? "",
            op: (vals.trigger_rule_op as TriggerOp) ?? "equals",
            value: vals.trigger_rule_value ?? "",
          };
        } else {
          next.trigger_rule = null;
        }
        // Only include object_type_api_name when set, to avoid spurious
        // dirty-state diffs against persisted frontmatter that lacks the key.
        const otName = (vals.object_type_api_name ?? "").trim();
        if (otName.length > 0) {
          next.object_type_api_name = otName;
        }
      }
      onChange(next);
    });
    return () => sub.unsubscribe();
  }, [form, isAgent, onChange]);

  // Reset when a different file is loaded.
  useEffect(() => {
    form.reset({
      name: value.name ?? "",
      description: value.description ?? "",
      trigger_type: value.trigger_type ?? "description",
      trigger_rule_field: value.trigger_rule?.field ?? "",
      trigger_rule_op: (value.trigger_rule?.op as TriggerOp) ?? "equals",
      trigger_rule_value: String(value.trigger_rule?.value ?? ""),
      object_type_api_name: value.object_type_api_name ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.name, value.description, value.trigger_type, value.object_type_api_name]);

  const sortedProperties = useMemo(
    () => [...(objectType?.property_types ?? [])].sort((a, b) => a.position - b.position),
    [objectType]
  );

  return (
    <Form {...form}>
      <div className="space-y-4 p-4">
        {/* Name */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={disabled}
                  placeholder="utility-vendors"
                  className="font-mono"
                />
              </FormControl>
              <p className="font-mono text-xs text-muted-foreground">
                URL slug, e.g. <code>utility-vendors</code>
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  disabled={disabled}
                  placeholder="Describe what this agent or skill does…"
                  className="resize-none"
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Object Type binding — hidden for Agent kind */}
        {!isAgent && (
          <FormField
            control={form.control}
            name="object_type_api_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-muted-foreground">Object Type</FormLabel>
                <Select
                  value={field.value || NONE}
                  onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                  disabled={disabled}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={NONE}>
                      <span className="text-muted-foreground">None</span>
                    </SelectItem>
                    {objectTypes.map((t) => (
                      <SelectItem key={t.id} value={t.api_name}>
                        {t.name}{" "}
                        <span className="text-muted-foreground font-mono text-xs">
                          ({t.api_name})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {objectTypeMissing && (
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Object Type <code className="font-mono">{objectTypeApiName}</code> no longer
                    exists.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Trigger type */}
        {!isAgent && (
          <FormField
            control={form.control}
            name="trigger_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-muted-foreground">Trigger type</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex gap-4"
                    disabled={disabled}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="description" id="trigger-description" />
                      <Label
                        htmlFor="trigger-description"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Description
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="rule" id="trigger-rule" />
                      <Label htmlFor="trigger-rule" className="text-sm font-normal cursor-pointer">
                        Rule
                      </Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Trigger rule block — only when trigger_type === 'rule' */}
        {!isAgent && triggerType === "rule" && (
          <div className="ml-4 space-y-3 border-l border-border pl-4">
            {/* Field: typed dropdown when Object Type set, free input otherwise */}
            <FormField
              control={form.control}
              name="trigger_rule_field"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground">Field</FormLabel>
                  {selectedObjectTypeId && sortedProperties.length > 0 ? (
                    <Select
                      value={field.value || ""}
                      onValueChange={field.onChange}
                      disabled={disabled}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full font-mono text-sm">
                          <SelectValue placeholder="Select a property…" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {sortedProperties.map((p) => (
                          <SelectItem key={p.id} value={p.api_name}>
                            {p.api_name}{" "}
                            <span className="text-muted-foreground text-xs">({p.kind})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <FormControl>
                      <Input
                        {...field}
                        disabled={disabled}
                        placeholder="vendor_name"
                        className="font-mono text-sm"
                      />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="trigger_rule_op"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground">Operator</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableOperators.map((op) => (
                        <SelectItem key={op} value={op}>
                          {op}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedPropertyKind && (
                    <p className="text-xs text-muted-foreground">
                      Limited to operators valid for kind{" "}
                      <code className="font-mono">{selectedPropertyKind}</code>.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="trigger_rule_value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground">Value</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={disabled}
                      placeholder="PG&E"
                      className="font-mono text-sm"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </Form>
  );
}
